import React, { useState, useRef, useCallback } from 'react';

// ─── Upscale via Canvas 2x (interpolación bilinear de alta calidad) ──────────
// Para preview y uso real. Pendiente: integrar TFLite ESRGAN para IA.
async function upscaleImageCanvas(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth * 2;
      canvas.height = img.naturalHeight * 2;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob failed'));
      }, 'image/jpeg', 0.95);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load error')); };
    img.src = url;
  });
}

// ─── Compartir via Web Share API (con fallback a descarga) ────────────────────
async function shareToWhatsApp(item, mode) {
  const fileBlob = item.upscaledBlob || item.originalFile;
  const fileName = item.originalFile?.name || `wabatch-${item.id}.jpg`;

  try {
    const file = new File([fileBlob], fileName, { type: fileBlob.type || 'image/jpeg' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: item.description || 'WaBatch',
        text: item.description || '',
        files: [file],
      });
      return true;
    }
  } catch (err) {
    console.error('Share error:', err);
  }

  // Fallback: navegador de escritorio sin Web Share API — descarga el archivo
  // para que el usuario lo suba a WhatsApp manualmente.
  const url = URL.createObjectURL(fileBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function WaBatch() {
  const [images, setImages] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [isUpscaling, setIsUpscaling] = useState(false);
  const [upscaleIdx, setUpscaleIdx] = useState(0);
  const [isSharing, setIsSharing] = useState(false);
  const fileInputRef = useRef(null);
  let nextId = useRef(0);

  // ── Selección de imágenes ────────────────────────────────────────────────
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newItems = files.map(file => ({
      id: nextId.current++,
      originalFile: file,
      previewUrl: URL.createObjectURL(file),
      upscaledBlob: null,
      upscaledUrl: null,
      description: '',
      selected: true,
      isUpscaling: false,
      isUpscaled: false,
    }));
    setImages(prev => [...prev, ...newItems]);
    e.target.value = '';
  }, []);

  const removeImage = useCallback((id) => {
    setImages(prev => {
      const item = prev.find(i => i.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      if (item?.upscaledUrl) URL.revokeObjectURL(item.upscaledUrl);
      return prev.filter(i => i.id !== id);
    });
  }, []);

  // ── Checkboxes ───────────────────────────────────────────────────────────
  const handleSelectAll = useCallback((checked) => {
    setSelectAll(checked);
    setImages(prev => prev.map(i => ({ ...i, selected: checked })));
  }, []);

  const handleToggle = useCallback((id, checked) => {
    setImages(prev => {
      const updated = prev.map(i => i.id === id ? { ...i, selected: checked } : i);
      setSelectAll(updated.every(i => i.selected));
      return updated;
    });
  }, []);

  const handleDescription = useCallback((id, text) => {
    setImages(prev => prev.map(i => i.id === id ? { ...i, description: text } : i));
  }, []);

  // ── Upscale ──────────────────────────────────────────────────────────────
  const handleUpscale = useCallback(async () => {
    const toProcess = images.filter(i => i.selected && !i.isUpscaled);
    if (!toProcess.length) return;

    setIsUpscaling(true);
    for (let idx = 0; idx < toProcess.length; idx++) {
      const item = toProcess[idx];
      setUpscaleIdx(idx);
      setImages(prev => prev.map(i => i.id === item.id ? { ...i, isUpscaling: true } : i));

      try {
        const blob = await upscaleImageCanvas(item.originalFile);
        const upscaledUrl = URL.createObjectURL(blob);
        setImages(prev => prev.map(i =>
          i.id === item.id
            ? { ...i, isUpscaling: false, isUpscaled: true, upscaledBlob: blob, upscaledUrl }
            : i
        ));
      } catch (err) {
        setImages(prev => prev.map(i => i.id === item.id ? { ...i, isUpscaling: false } : i));
      }
    }
    setIsUpscaling(false);
  }, [images]);

  // ── Compartir ────────────────────────────────────────────────────────────
  const handleShare = useCallback(async (mode) => {
    if (!images.length || isSharing) return;
    setIsSharing(true);
    for (const item of images) {
      await shareToWhatsApp(item, mode);
      // Pequeño delay para que el share sheet se cierre entre imágenes
      await new Promise(r => setTimeout(r, 600));
    }
    setIsSharing(false);
  }, [images, isSharing]);

  const upscaleCount = images.filter(i => i.selected && !i.isUpscaled).length;

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: 'linear-gradient(180deg, #1a6b5a 0%, #0d3b6e 100%)' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex-shrink-0" style={{ background: 'linear-gradient(90deg, #16a085, #1abc9c)', padding: '14px 20px' }}>
        <span style={{ fontSize: 26, fontWeight: 900, color: '#fff', letterSpacing: 1 }}>WaBatch</span>
      </div>

      {/* ── Barra de control ────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2">
        {/* Seleccionar todo */}
        <label className="flex items-center gap-1 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selectAll}
            onChange={e => handleSelectAll(e.target.checked)}
            className="w-5 h-5 accent-green-400 cursor-pointer"
          />
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>SELECCIONAR TODO</span>
        </label>

        <div className="flex gap-2">
          {/* Agregar imágenes */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUpscaling}
            style={{
              background: '#1abc9c', color: '#fff', fontWeight: 900,
              fontSize: 18, border: 'none', borderRadius: 10,
              padding: '6px 14px', cursor: 'pointer', lineHeight: 1,
              opacity: isUpscaling ? 0.5 : 1,
            }}
            title="Agregar imágenes"
          >+</button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {/* Upscale */}
          <button
            onClick={handleUpscale}
            disabled={isUpscaling || !upscaleCount}
            style={{
              background: '#e74c3c', color: '#fff', fontWeight: 900,
              fontSize: 13, border: 'none', borderRadius: 10,
              padding: '6px 12px', cursor: 'pointer',
              opacity: (isUpscaling || !upscaleCount) ? 0.5 : 1,
              display: 'flex', alignItems: 'center', gap: 4,
            }}
          >
            ✨ {isUpscaling ? `${upscaleIdx + 1}/${upscaleCount}` : 'UPSCALE'}
          </button>
        </div>
      </div>

      {/* ── Barra de progreso upscale ─────────────────────────────────── */}
      {isUpscaling && (
        <div className="flex-shrink-0 px-3 pb-1">
          <div style={{ background: 'rgba(255,255,255,0.2)', borderRadius: 4, height: 5, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', background: '#2ecc71', borderRadius: 4,
                width: `${((upscaleIdx + 1) / images.filter(i => i.selected && !i.isUpscaled).length) * 100}%`,
                transition: 'width 0.3s',
              }}
            />
          </div>
          <p style={{ color: '#fff', fontSize: 11, marginTop: 3 }}>
            Procesando imagen {upscaleIdx + 1}...
          </p>
        </div>
      )}

      {/* ── Lista de imágenes ────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 8 }}>
        {images.length === 0 ? (
          <EmptyState onAdd={() => fileInputRef.current?.click()} />
        ) : (
          images.map(item => (
            <ImageRow
              key={item.id}
              item={item}
              onToggle={handleToggle}
              onDescriptionChange={handleDescription}
              onRemove={removeImage}
            />
          ))
        )}
      </div>

      {/* ── Botones principales ──────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex gap-3 px-4 py-3"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)' }}
      >
        <ActionButton
          label="ESTADO"
          color="#2ecc71"
          disabled={!images.length || isSharing || isUpscaling}
          onClick={() => handleShare('status')}
        />
        <ActionButton
          label="CANAL"
          color="#00bcd4"
          disabled={!images.length || isSharing || isUpscaling}
          onClick={() => handleShare('channel')}
        />
      </div>
    </div>
  );
}

// ─── Fila de imagen ────────────────────────────────────────────────────────────
function ImageRow({ item, onToggle, onDescriptionChange, onRemove }) {
  const displayUrl = item.upscaledUrl || item.previewUrl;

  return (
    <div
      style={{
        margin: '5px 12px',
        background: '#e8f8f5',
        border: '1.5px solid #aeeee4',
        borderRadius: 14,
        padding: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
      }}
    >
      {/* Checkbox */}
      <input
        type="checkbox"
        checked={item.selected}
        onChange={e => onToggle(item.id, e.target.checked)}
        style={{ width: 20, height: 20, accentColor: '#2ecc71', flexShrink: 0, cursor: 'pointer' }}
      />

      {/* Thumbnail */}
      <div style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
        <img
          src={displayUrl}
          alt=""
          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 10, border: '1px solid #aeeee4' }}
        />
        {item.isUpscaling && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)', borderRadius: 10,
          }}>
            <div style={{
              width: 28, height: 28, border: '3px solid #2ecc71',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
          </div>
        )}
        {item.isUpscaled && !item.isUpscaling && (
          <div style={{
            position: 'absolute', top: 3, right: 3,
            background: '#2ecc71', borderRadius: 6, padding: '2px 4px',
            fontSize: 9, color: '#fff', fontWeight: 700,
          }}>2x</div>
        )}
      </div>

      {/* Campo descripción */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <textarea
          value={item.description}
          onChange={e => onDescriptionChange(item.id, e.target.value)}
          placeholder="Descripción..."
          rows={3}
          style={{
            width: '100%',
            background: 'linear-gradient(180deg, #76d7a0, #58d68d)',
            border: '1.5px solid #27ae60',
            borderRadius: 8,
            padding: '6px 8px',
            fontSize: 13,
            color: '#1c2833',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />
        {item.isUpscaled && (
          <span style={{ fontSize: 10, color: '#27ae60', fontWeight: 600 }}>✓ 2x upscale aplicado</span>
        )}
      </div>

      {/* Eliminar */}
      <button
        onClick={() => onRemove(item.id)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 18, color: '#e74c3c', flexShrink: 0, padding: '0 2px',
          lineHeight: 1,
        }}
        title="Eliminar"
      >×</button>
    </div>
  );
}

// ─── Estado vacío ──────────────────────────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: 32 }}>
      <button
        onClick={onAdd}
        style={{
          width: 100, height: 100,
          background: 'rgba(255,255,255,0.15)',
          border: '2px dashed rgba(255,255,255,0.4)',
          borderRadius: 20, cursor: 'pointer',
          fontSize: 42, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >🖼️</button>
      <p style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: 15 }}>Toca para seleccionar imágenes</p>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Podés elegir varias a la vez</p>
    </div>
  );
}

// ─── Botón de acción inferior ──────────────────────────────────────────────────
function ActionButton({ label, color, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex: 1, height: 54,
        background: disabled ? color + '80' : color,
        color: '#fff', fontWeight: 900, fontSize: 16,
        border: 'none', borderRadius: 14, cursor: disabled ? 'not-allowed' : 'pointer',
        letterSpacing: 1, boxShadow: disabled ? 'none' : '0 4px 12px rgba(0,0,0,0.3)',
        transition: 'opacity 0.2s',
      }}
    >{label}</button>
  );
}
